import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { isLoggedIn, getSession } from '../utils/session';
import './FlowDemo.css';

/* ── Virtual avatar (CSS) ── */
function AvatarFigure({ dressed }) {
  return (
    <div className={`avatar ${dressed ? 'avatar--dressed' : ''}`}>
      <div className="avatar__shadow" aria-hidden="true" />
      <div className="avatar__head">
        <div className="avatar__hair" />
        <div className="avatar__face">
          <div className="avatar__eyes">
            <span className="avatar__eye" />
            <span className="avatar__eye" />
          </div>
          <div className="avatar__mouth" />
        </div>
      </div>

      <div className="avatar__neck" />

      <div className="avatar__torso">
        <div className="avatar__shoulder avatar__shoulder--left" />
        <div className="avatar__shoulder avatar__shoulder--right" />
        <div className="avatar__body" />

        {/* Garment overlay appears in dressed state */}
        <div className="garment-overlay" aria-hidden="true">
          <div className="garment-overlay__collar" />
          <div className="garment-overlay__shirt" />
          <div className="garment-overlay__sleeve garment-overlay__sleeve--left" />
          <div className="garment-overlay__sleeve garment-overlay__sleeve--right" />
          <div className="garment-overlay__pants" />
          <div className="garment-overlay__shine" />
        </div>
      </div>

      <div className="avatar__legs">
        <div className="avatar__leg" />
        <div className="avatar__leg" />
      </div>
    </div>
  );
}

/* ── Garment card (CSS) ── */
function GarmentFigure() {
  return (
    <div className="garment">
      <div className="garment__hanger" aria-hidden="true">
        <div className="garment__hook" />
        <div className="garment__bar" />
      </div>
      <div className="garment__card">
        <div className="garment__preview">
          <div className="garment__fabric" />
          <div className="garment__shape">
            <div className="garment__neck" />
            <div className="garment__shirt">
              <div className="garment__sleeve garment__sleeve--left" />
              <div className="garment__sleeve garment__sleeve--right" />
              <div className="garment__shirt-body" />
            </div>
            <div className="garment__pants">
              <div className="garment__pant-leg garment__pant-leg--left" />
              <div className="garment__pant-leg garment__pant-leg--right" />
            </div>
          </div>
          <div className="garment__glow" />
        </div>
        <div className="garment__meta">
          <div className="garment__name">Techwear Shirt + Pants</div>
          <div className="garment__chips" aria-hidden="true">
            <span>AI‑ready</span>
            <span>Fit‑aware</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function FlowDemo() {
  const navigate = useNavigate();

  /* ── Route guard: redirect to home if not logged in ── */
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/', { replace: true, state: { requireLogin: true } });
    }
  }, [navigate]);

  useSEO({
    title: 'AI Virtual Try On Demo | PutOn AI',
    description: 'See how AI virtual try on works. Upload your photo and any garment to preview clothing fit online in seconds.',
    canonical: 'https://putonai.com/try-on',
    ogImage: 'https://putonai.com/logo.png',
  });

  const session = getSession();

  // 0 = show inputs, 1 = processing, 2 = result
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [sparkles, setSparkles] = useState(false);

  /* Auto-advance stages */
  useEffect(() => {
    setProgress(0);
    setSparkles(false);

    if (stage === 0) {
      const t = setTimeout(() => setStage(1), 2600);
      return () => clearTimeout(t);
    }
    if (stage === 1) {
      const t = setTimeout(() => { setStage(2); setSparkles(true); }, 3000);
      return () => clearTimeout(t);
    }
    if (stage === 2) {
      const t = setTimeout(() => setStage(0), 3800);
      return () => clearTimeout(t);
    }
  }, [stage]);

  /* Progress bar fill during stage 1 */
  useEffect(() => {
    if (stage !== 1) return;
    let p = 0;
    const iv = setInterval(() => {
      p += 100 / 58; // fill in ~2.9s (58 ticks × 50ms)
      setProgress(Math.min(p, 100));
      if (p >= 100) clearInterval(iv);
    }, 50);
    return () => clearInterval(iv);
  }, [stage]);

  const STAGE_LABELS = [
    'Upload your photo & choose a garment',
    'AI is processing your virtual try-on…',
    "Here's how you'd look in that dress! \u2728",
  ];

  return (
    <div className="flow-page">
      <header className="flow-nav">
        <Link to="/" className="flow-back" aria-label="Go back to PutOn AI homepage">
          <ArrowLeft size={18} aria-hidden="true" />
          Back
        </Link>
        <span className="flow-brand" aria-label="PutOn AI Flow Demo">PutOn<span>AI</span> · Flow Demo</span>
        {session?.email && (
          <span className="flow-user" title={`Logged in as ${session.email}`}>
            👤 {session.email}
          </span>
        )}
      </header>

      <main className="flow-main" id="main-content">
        {/* ── Futuristic hero ── */}
        <section className="tryon-hero" aria-label="PutOn AI launching soon">
          <div className="tryon-hero__bg" aria-hidden="true" />
          <div className="tryon-hero__content">
            <div className="tryon-hero__badge">
              <span className="tryon-hero__pulse" aria-hidden="true" />
              AI Virtual Try‑On · Coming Soon
            </div>
            <h1 className="tryon-hero__title">
              PutOn <span className="tryon-hero__title-ai">AI</span> is launching very soon
            </h1>
            <p className="tryon-hero__subtitle">
              A photorealistic, AI‑powered fitting room that understands body shape, fabric drape, and lighting — so you
              can try outfits instantly.
            </p>

            <div className="tryon-hero__stats" role="list" aria-label="AI capabilities">
              <div className="tryon-stat" role="listitem">
                <div className="tryon-stat__label">Neural Fit</div>
                <div className="tryon-stat__value">Body-aware</div>
              </div>
              <div className="tryon-stat" role="listitem">
                <div className="tryon-stat__label">Texture</div>
                <div className="tryon-stat__value">Fabric drape</div>
              </div>
              <div className="tryon-stat" role="listitem">
                <div className="tryon-stat__label">Vision</div>
                <div className="tryon-stat__value">Lighting match</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── See it in action ── */}
        <h2 className="flow-title flow-title--section">See It In Action</h2>
        <p className="flow-lead">
          Watch how PutOn AI virtually dresses you in any outfit — in seconds.
        </p>

        {/* ── Animation Card ── */}
        <div className="flow-card">

          {/* Stage indicator tabs */}
          <div className="flow-tabs" role="tablist" aria-label="Try-on demo stages">
            {['Photo + Garment', 'AI Processing', 'Result'].map((label, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={stage === i}
                aria-label={`Stage ${i + 1}: ${label}${stage > i ? ' — completed' : stage === i ? ' — current' : ''}`}
                className={`flow-tab ${stage === i ? 'flow-tab--active' : ''} ${stage > i ? 'flow-tab--done' : ''}`}
                onClick={() => setStage(i)}
              >
                <span className="flow-tab__dot" aria-hidden="true">{stage > i ? '✓' : i + 1}</span>
                <span className="flow-tab__label">{label}</span>
              </button>
            ))}
          </div>

          {/* ── Viewport ── */}
          <div className="flow-viewport" role="region" aria-label="Virtual try-on animation" aria-live="polite">

            {/* ── STAGE 0: Inputs ── */}
            <div className={`flow-stage flow-stage--inputs ${stage === 0 ? 'flow-stage--visible' : ''}`}>
              {/* Person photo */}
              <div className="photo-card photo-card--person">
                <div className="photo-card__frame">
                  <AvatarFigure dressed={false} />
                </div>
                <div className="photo-card__label">
                  <span className="photo-card__dot photo-card__dot--green" />
                  Your Photo
                </div>
              </div>

              {/* Plus sign */}
              <div className="flow-operator">
                <div className="flow-operator__circle">+</div>
              </div>

              {/* Dress product */}
              <div className="photo-card photo-card--dress">
                <div className="photo-card__frame photo-card__frame--product">
                  <GarmentFigure />
                </div>
                <div className="photo-card__label">
                  <span className="photo-card__dot photo-card__dot--pink" />
                  Outfit
                </div>
              </div>
            </div>

            {/* ── STAGE 1: Processing ── */}
            <div className={`flow-stage flow-stage--processing ${stage === 1 ? 'flow-stage--visible' : ''}`}>
              <div className="processing-area">
                {/* Faded person */}
                <div className="processing-input processing-input--left">
                  <AvatarFigure dressed={false} />
                </div>

                {/* AI core */}
                <div className="ai-core">
                  <div className="ai-core__ring ai-core__ring--1" />
                  <div className="ai-core__ring ai-core__ring--2" />
                  <div className="ai-core__ring ai-core__ring--3" />
                  <div className="ai-core__center">
                    <span className="ai-core__icon">🤖</span>
                  </div>
                </div>

                {/* Faded dress */}
                <div className="processing-input processing-input--right">
                  <GarmentFigure />
                </div>
              </div>

              {/* Progress bar */}
              <div className="progress-track">
                <div className="progress-track__labels">
                  <span>Analysing body shape…</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="progress-track__bar">
                  <div
                    className="progress-track__fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="progress-steps">
                  {['Body detection', 'Garment mapping', 'Lighting match', 'Final render'].map((s, i) => (
                    <span
                      key={s}
                      className={`progress-step ${progress >= (i + 1) * 25 ? 'progress-step--done' : ''}`}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── STAGE 2: Result ── */}
            <div className={`flow-stage flow-stage--result ${stage === 2 ? 'flow-stage--visible' : ''}`}>
              <div className="result-area">
                {sparkles && (
                  <>
                    <div className="sparkle sparkle--1">✦</div>
                    <div className="sparkle sparkle--2">✦</div>
                    <div className="sparkle sparkle--3">✦</div>
                    <div className="sparkle sparkle--4">✦</div>
                  </>
                )}
                <div className="result-card">
                  <div className="result-card__frame">
                    <AvatarFigure dressed={true} />
                  </div>
                  <div className="result-card__badge">
                    <Sparkles size={11} /> AI Try-On
                  </div>
                </div>

                <div className="result-compare">
                  <div className="result-compare__item">
                    <div className="result-compare__thumb result-compare__thumb--before">
                      <AvatarFigure dressed={false} />
                    </div>
                    <span>Before</span>
                  </div>
                  <div className="result-compare__arrow">→</div>
                  <div className="result-compare__item">
                    <div className="result-compare__thumb result-compare__thumb--after">
                      <AvatarFigure dressed={true} />
                    </div>
                    <span>After</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Caption */}
          <p className="flow-caption" aria-live="polite" aria-atomic="true">{STAGE_LABELS[stage]}</p>
        </div>

        {/* ── Thank you / coming soon ── */}
        <section className="tryon-thanks" aria-labelledby="tryon-thanks-heading">
          <div className="tryon-thanks__card">
            <div className="tryon-thanks__glow" aria-hidden="true" />
            <div className="tryon-thanks__icon" aria-hidden="true">🚀</div>
            <h2 id="tryon-thanks-heading">Thank you for trying the demo</h2>
            <p>
              We’re building the full AI try‑on experience right now. The next release adds real photo uploads, garment
              extraction, and a production‑grade rendering pipeline.
            </p>
            <div className="tryon-thanks__cta">
              <Link to="/" className="tryon-thanks__btn" aria-label="Return to PutOn AI homepage">
                ← Back to Home
              </Link>
              <span className="tryon-thanks__note">We’ll launch very soon.</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
